import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Table,
  Header,
  Message,
  Modal,
  Form,
  Input,
  Dimmer,
  Loader,
  Icon,
  Pagination
} from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import GlassTable from '../../components/Common/GlassTable';
import { SimulationModal } from '../../components/Simulation/SimulationModal';
import { simulationService } from '../../services/simulation/SimulationService';
import { whatsAppClientImportService, type CsvImportResult } from '../../services/whatsapp/WhatsAppClientImportService';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import { getDefaultButtonStyle, getButtonHoverEffects } from '../../styles/designSystem';
import { GlassmorphismTheme } from '../../styles/glassmorphism';
import { SimulationClientDto, SimulationPagedResultDto } from '../../types/simulation.types';
import { canAccessSimulation } from '../../utils/simulationAccess';

const SimulationPage: React.FC = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  const [clientes, setClientes] = useState<SimulationClientDto[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<CsvImportResult | null>(null);

  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Estado de busca
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const isAdmin = user?.roles?.includes('Admin');
  const canAccess = canAccessSimulation(user?.roles);

  const loadClientes = useCallback(async (page: number = 1, search?: string) => {
    setLoading(true);
    try {
      const result = await simulationService.getClients(page, pageSize, search);
      setClientes(result.items);
      setTotalPages(result.totalPages);
      setTotalCount(result.totalCount);
      setCurrentPage(result.pageNumber);
    } catch (error: any) {
      showError(error.message || 'Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  }, [pageSize, showError]);

  useEffect(() => {
    if (canAccess) {
      loadClientes(1, searchTerm);
    }
  }, [canAccess, searchTerm]);

  const handlePageChange = (_: any, data: any) => {
    const newPage = data.activePage as number;
    loadClientes(newPage, searchTerm);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setCsvFile(e.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!csvFile) {
      showError('Selecione um arquivo CSV');
      return;
    }

    try {
      setImporting(true);
      const result = await whatsAppClientImportService.importClientsFromCsv(csvFile);
      setImportResult(result);

      const errCount = result.erros?.length ?? 0;
      const touched = result.inseridos + result.atualizados;

      if (errCount === 0 && touched > 0) {
        showSuccess(
          `✅ ${result.inseridos} inseridos, ${result.atualizados} atualizados — ` +
            `${result.linhasInseridas} linhas novas, ${result.linhasAtualizadas} linhas atualizadas.`
        );
        await loadClientes(1, searchTerm);
      } else if (errCount > 0) {
        showError(`⚠️ Importação concluída com ${errCount} mensagem(ns) de erro (veja detalhes abaixo).`);
        if (touched > 0) await loadClientes(1, searchTerm);
      } else if (touched === 0 && errCount === 0) {
        showError('Nenhum registro foi processado. Confira o formato do CSV.');
      }
    } catch (error: any) {
      showError(error.message || 'Erro ao importar arquivo');
    } finally {
      setImporting(false);
    }
  };

  const handleSimulate = (clienteId: string) => {
    setSelectedClientId(clienteId);
    setModalOpen(true);
  };

  // Se não for Admin, mostrar apenas mensagem informativa
  if (!isAdmin) {
    return (
      <GlassPageWrapper title="Simulação de Planos" icon="calculator">
        <Message info>
          <Message.Header>Acesso à Lista de Clientes</Message.Header>
          <p>Use o botão flutuante de simulação para buscar clientes por CNPJ ou Razão Social.</p>
          <p>A lista completa de clientes e a importação CSV ficam disponíveis apenas para administradores.</p>
        </Message>
      </GlassPageWrapper>
    );
  }

  return (
    <GlassPageWrapper title="Simulação de Planos" icon="calculator">
      <Dimmer active={loading} inverted>
        <Loader content="Carregando clientes..." />
      </Dimmer>

      {/* Info Box */}
      <Message info>
        <Message.Header>📋 Como usar a Simulação</Message.Header>
        <Message.List>
          <Message.Item>Importe clientes via CSV (formato Clientes WhatsApp) usando o botão abaixo</Message.Item>
          <Message.Item>Clique em "Simular" para abrir o modal de simulação</Message.Item>
          <Message.Item>Selecione as linhas para renovação (checkboxes marcados por padrão)</Message.Item>
          <Message.Item>Adicione novas linhas para planos de aditivo</Message.Item>
          <Message.Item>Calcule a simulação para ver valores e diferenças</Message.Item>
        </Message.List>
      </Message>

      {/* Import Button and Search */}
      <div style={{ marginTop: '20px', marginBottom: '20px', display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          primary
          icon="upload"
          content="Importar CSV"
          onClick={() => setImportModalOpen(true)}
          style={getDefaultButtonStyle()}
          onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => Object.assign((e.currentTarget as HTMLElement).style, getButtonHoverEffects())}
          onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => Object.assign((e.currentTarget as HTMLElement).style, getDefaultButtonStyle())}
        />

        {/* Campo de Busca */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flex: 1, maxWidth: '500px' }}>
          <Input
            placeholder="Buscar por CNPJ ou Razão Social..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ flex: 1 }}
            icon={searchInput ? <Icon name="close" link onClick={handleClearSearch} /> : undefined}
          />
          <Button
            primary
            icon="search"
            content="Buscar"
            onClick={handleSearch}
            disabled={loading}
          />
        </div>

        {/* Info de paginação */}
        <div style={{ color: '#666', fontSize: '14px' }}>
          {totalCount > 0 && (
            <span>
              Mostrando {clientes.length} de {totalCount.toLocaleString()} clientes
              {searchTerm && <span> (filtrado por: "{searchTerm}")</span>}
            </span>
          )}
        </div>
      </div>

      {/* Result Box */}
      {importResult && (
        <Message
          positive={importResult.erros.length === 0}
          warning={importResult.erros.length > 0}
          style={{ marginBottom: '20px' }}
        >
          <Message.Header>
            {importResult.erros.length === 0 ? '✅ Importação concluída' : '⚠️ Importação com avisos/erros'}
          </Message.Header>
          <p>
            <strong>Inseridos:</strong> {importResult.inseridos} · <strong>Atualizados:</strong>{' '}
            {importResult.atualizados} · <strong>Linhas novas:</strong> {importResult.linhasInseridas} ·{' '}
            <strong>Linhas atualizadas:</strong> {importResult.linhasAtualizadas}
          </p>
          {importResult.erros.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <p>
                <strong>Erros ({importResult.erros.length}):</strong>
              </p>
              <ul style={{ maxHeight: 200, overflow: 'auto' }}>
                {importResult.erros.slice(0, 15).map((msg, idx) => (
                  <li key={idx}>{msg}</li>
                ))}
                {importResult.erros.length > 15 && (
                  <li>… e mais {importResult.erros.length - 15} mensagens</li>
                )}
              </ul>
            </div>
          )}
        </Message>
      )}

      {/* Clients Table */}
      <GlassTable celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>CNPJ</Table.HeaderCell>
            <Table.HeaderCell>Razão Social</Table.HeaderCell>
            <Table.HeaderCell>Admin</Table.HeaderCell>
            <Table.HeaderCell>Linhas</Table.HeaderCell>
            <Table.HeaderCell>Valor Fatura</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Ações</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {clientes.map(cliente => (
            <Table.Row key={cliente.id}>
              <Table.Cell>{cliente.cnpj}</Table.Cell>
              <Table.Cell>{cliente.razaoSocial}</Table.Cell>
              <Table.Cell>{cliente.nomeAdm}</Table.Cell>
              <Table.Cell>{cliente.linhas.length}</Table.Cell>
              <Table.Cell>R$ {cliente.valorFatura.toFixed(2)}</Table.Cell>
              <Table.Cell>
                <span style={{ 
                  color: (typeof cliente.statusFluxo === 'string' ? cliente.statusFluxo : 'PENDENTE') === 'PENDENTE' ? '#f39c12' : '#27ae60',
                  fontWeight: 'bold'
                }}>
                  {typeof cliente.statusFluxo === 'string' 
                    ? cliente.statusFluxo 
                    : ['PENDENTE', 'TEMPLATE_ENVIADO', 'AGUARDANDO_RESPOSTA', 'EM_FLUXO_AUTOMATIZADO', 'ATENDIMENTO_HUMANO', 'FINALIZADO'][cliente.statusFluxo as number] || 'DESCONHECIDO'}
                </span>
              </Table.Cell>
              <Table.Cell>
                <Button
                  primary
                  size="small"
                  icon="calculator"
                  content="Simular"
                  onClick={() => handleSimulate(cliente.id)}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </GlassTable>

      {clientes.length === 0 && !loading && (
        <Message info>
          <Message.Header>Nenhum cliente encontrado</Message.Header>
          <p>{searchTerm ? `Nenhum cliente encontrado para "${searchTerm}".` : 'Importe clientes via CSV para começar a simular planos.'}</p>
        </Message>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '20px' }}>
          <Pagination
            activePage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            ellipsisItem={{ content: <Icon name="ellipsis horizontal" />, icon: true }}
            firstItem={{ content: <Icon name="angle double left" />, icon: true }}
            lastItem={{ content: <Icon name="angle double right" />, icon: true }}
            prevItem={{ content: <Icon name="angle left" />, icon: true }}
            nextItem={{ content: <Icon name="angle right" />, icon: true }}
          />
        </div>
      )}

      {/* Simulation Modal */}
      {selectedClientId && (
        <SimulationModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          clienteId={selectedClientId}
        />
      )}

      {/* Import Modal */}
      <Modal open={importModalOpen} onClose={() => setImportModalOpen(false)} size="small">
        <Modal.Header>Importar Clientes WhatsApp</Modal.Header>
        <Modal.Content>
          <Dimmer active={importing} inverted>
            <Loader content="Importando..." />
          </Dimmer>

          <Form>
            <Form.Field required>
              <label htmlFor="csv-file-input-sim">Arquivo CSV</label>
              <Input
                id="csv-file-input-sim"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                disabled={importing}
              />
              <small style={{ color: '#999', marginTop: '5px', display: 'block' }}>
                Mesmo formato da importação em Clientes WhatsApp (CSV largo com LINHA 1…20).
              </small>
            </Form.Field>

            {csvFile && (
              <Message info>
                <Message.Header>Arquivo Selecionado</Message.Header>
                <p>{csvFile.name}</p>
              </Message>
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => {
            setImportModalOpen(false);
            setCsvFile(null);
            setImportResult(null);
          }} disabled={importing}>
            Cancelar
          </Button>
          <Button
            primary
            onClick={handleImport}
            disabled={!csvFile || importing}
            loading={importing}
          >
            Importar
          </Button>
        </Modal.Actions>
      </Modal>
    </GlassPageWrapper>
  );
};

export default SimulationPage;
